import React from 'react';
import { useNavigate } from 'react-router-dom';
import AboutIntroCards from './AboutIntroCards'; 

export default function AboutPage({ language = 'en' }) {
  const navigate = useNavigate();

  const content = {
    en: `YourLostandFound was born in April 2024 in Florida, USA, as a concrete response to this everyday issue. Our initial mission was to offer reliable, accessible, and user-friendly tools for identifying, reporting, and recovering school items. Over time, we discovered that the potential of this tool went far beyond classrooms: it could extend to any space where ownership and identity matter.

We have closely observed existing solutions in the market and, while we recognize their contributions, we firmly believe that YourLostandFound represents a different and necessary proposal by combining intuitive technology, user-centered design, and a deeply human vision. More than a digital solution, it's an invitation to transform the culture of "lost and found" into a collaborative experience.

We believe in the power of online communities and people’s goodwill.
Our goal is not just to succeed: it’s to make a difference in every community we reach.

Our Mission
To create a reliable and accessible ecosystem for identifying, reporting, and recovering lost belongings, strengthening the sense of community and shared responsibility.

Our Vision
To become the reference platform in the Americas and worldwide for the intelligent and humane handling of lost items, supported by technology and solidarity.

Our Objectives
- Simplify the return of lost items in schools, homes, and businesses.
- Foster a culture of community responsibility.
- Integrate technological solutions that ensure privacy, traceability, and ease of use.
- Scale our model to sectors like tourism, events, transportation, and logistics.

Opportunities for Strategic Allies
We know many brands already offer high-quality products in sectors like stationery, uniforms, backpacks, sports, and school supplies. At YourLostandFound, we see a great opportunity for these manufacturers and distributors to enhance their value proposition by integrating our technology as a direct bridge to their end customers.

Through our solutions, your products not only gain smart functionality but become tools for loyalty, post-sale service, and brand differentiation.

We are open to building alliances with companies that share our vision: products that don’t get lost, brands that do stay.`,

    es: `YourLostandFound nació en abril de 2024 en Florida, Estados Unidos, como una respuesta concreta a esta necesidad cotidiana. Nuestra misión inicial fue ofrecer mecanismos confiables, accesibles y fáciles de usar para la identificación, reporte y recuperación de artículos escolares. Con el tiempo, descubrimos que el potencial de esta herramienta iba mucho más allá de las aulas: podía extenderse a cualquier ámbito donde la pertenencia y la identidad importen.

Hemos observado de cerca las soluciones existentes en el mercado y, aunque reconocemos sus aportes, creemos firmemente que YourLostandFound representa una propuesta distinta y necesaria, al combinar tecnología intuitiva, diseño centrado en el usuario y una visión profundamente humana. Más que una solución digital, es una invitación a transformar la cultura del “objeto perdido” en una experiencia colaborativa.

Creemos en el poder de las comunidades en línea y en la buena voluntad de las personas.
Nuestra meta no es solo alcanzar el éxito: es hacer la diferencia en cada comunidad que tocamos.

Nuestra Misión
Crear un ecosistema confiable y accesible para identificar, reportar y recuperar pertenencias perdidas, fortaleciendo el sentido de comunidad y responsabilidad compartida.

Nuestra Visión
Convertirnos en la plataforma de referencia en América y el mundo para el manejo inteligente y humano de objetos perdidos, apoyándonos en la tecnología y la solidaridad.

Nuestros Objetivos
- Simplificar la devolución de objetos perdidos en escuelas, hogares y empresas.
- Fomentar una cultura de responsabilidad comunitaria.
- Integrar soluciones tecnológicas que garanticen privacidad, trazabilidad y facilidad de uso.
- Escalar nuestro modelo a sectores como turismo, eventos, transporte y logística.

Oportunidades para aliados estratégicos
Sabemos que muchas marcas ya ofrecen productos de alta calidad en sectores como papelería, uniformes, mochilas, artículos deportivos y escolares. En YourLostandFound vemos una gran oportunidad para que esos fabricantes y distribuidores potencien aún más su propuesta de valor, integrando nuestra tecnología como un puente directo con sus clientes finales.

A través de nuestras soluciones, sus productos no solo ganan una función inteligente, sino que se convierten en vehículos de fidelización, servicio postventa y diferenciación de marca.

Estamos abiertos a construir alianzas con empresas que compartan nuestra visión: productos que no se pierden, marcas que sí se quedan.`,

    pt: `YourLostandFound nasceu em abril de 2024 na Flórida, Estados Unidos, como uma resposta concreta a essa necessidade cotidiana. Nossa missão inicial foi oferecer mecanismos confiáveis, acessíveis e fáceis de usar para identificação, reporte e recuperação de itens escolares. Com o tempo, descobrimos que o potencial desta ferramenta ia além das salas de aula: ela poderia ser usada em qualquer ambiente onde pertencimento e identidade importam.

Observamos de perto as soluções existentes no mercado e, embora reconheçamos suas contribuições, acreditamos firmemente que o YourLostandFound representa uma proposta distinta e necessária, ao combinar tecnologia intuitiva, design centrado no usuário e uma visão profundamente humana. Mais que uma solução digital, é um convite para transformar a cultura do “objeto perdido” em uma experiência colaborativa.

Acreditamos no poder das comunidades online e na boa vontade das pessoas.
Nosso objetivo não é apenas alcançar o sucesso: é fazer a diferença em cada comunidade que tocamos.

Nossa Missão
Criar um ecossistema confiável e acessível para identificar, reportar e recuperar pertences perdidos, fortalecendo o senso de comunidade e responsabilidade compartilhada.

Nossa Visão
Tornar-se a plataforma de referência nas Américas e no mundo para o gerenciamento inteligente e humano de objetos perdidos, com base na tecnologia e na solidariedade.

Nossos Objetivos
- Simplificar a devolução de objetos perdidos em escolas, lares e empresas.
- Promover uma cultura de responsabilidade comunitária.
- Integrar soluções tecnológicas que garantam privacidade, rastreabilidade e facilidade de uso.
- Escalar nosso modelo para setores como turismo, eventos, transporte e logística.

Oportunidades para Parceiros Estratégicos
Sabemos que muitas marcas já oferecem produtos de alta qualidade em setores como papelaria, uniformes, mochilas, artigos esportivos e escolares. No YourLostandFound vemos uma grande oportunidade para que esses fabricantes e distribuidores fortaleçam ainda mais sua proposta de valor, integrando nossa tecnologia como uma ponte direta com seus clientes finais.

Com nossas soluções, seus produtos não apenas ganham funcionalidade inteligente, mas também se tornam ferramentas de fidelização, serviço pós-venda e diferenciação de marca.

Estamos abertos a construir parcerias com empresas que compartilhem nossa visão: produtos que não se perdem, marcas que permanecem.`
  };

  return (
  <>
    {/* Encabezado principal */}
    <section
      id="About"
      className="bg-[#48887B] py-12 text-center scroll-mt-[120px] pt-[120px]"
    >
      <h2 className="text-white text-3xl md:text-5xl font-bold">
        {language === 'es' ? 'Acerca de Nosotros' : language === 'pt' ? 'Sobre Nós' : 'About Us'}
      </h2>
    </section>

    {/* Bloque introductorio de tarjetas */}
    <AboutIntroCards language={language} />

    {/* Contenido extenso de texto */}
    <div className="max-w-5xl mx-auto py-12 px-6">
      <p className="whitespace-pre-line text-gray-800 leading-relaxed">{content[language]}</p>

      <button
        onClick={() => navigate('/')}
        className="mt-8 px-6 py-2 bg-[#48887B] text-white rounded-lg hover:bg-[#3b726a] transition"
      >
        {language === 'es' ? 'Volver' : language === 'pt' ? 'Voltar' : 'Back'}
      </button>
    </div>
  </>
);

  
}
